import { AfterViewInit, Component, DestroyRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { RootService } from '@app/core/root.service';
import { ItemProps } from '@app/interfaces';
import { AbstractControl, FormGroup, Validators } from '@angular/forms';
import { transition, trigger, useAnimation } from '@angular/animations';
import { HideAnimation, showAnimation } from '@app/shared/animations/transform-opacity';
import { PairsPipe } from '@app/shared/pipes/pairs.pipe';

import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';

// Angular Material Modules

// Third-party Modules
import { NgSelectModule } from '@ng-select/ng-select';

// Depending on whether rollup is used, moment needs to be imported differently.
// Since Moment.js doesn't have a default export, we normally need to import using the `* as`
// syntax. However, rollup creates a synthetic default module and we thus need to import it using
// the `default as` syntax.
import * as _moment from 'moment';
// tslint:disable-next-line:no-duplicate-imports
import { defaultFormat as _rollupMoment } from 'moment';
import { TranslateService } from '@ngx-translate/core';
import { AppHelper } from '@app/core/classes/app-helper';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { DrawMapComponent } from '@app/shared/components/draw-map/draw-map.component';
import { DateTimeComponent } from '@app/shared/components/date-time/date-time.component';
import { SHARED_IMPORTS } from '@app/shared/shared.imports';
import { CheckboxsComponent } from '../checkboxs/checkboxs.component';
import { EnglishMomentDateAdapter } from '@app/core/custom-date-adapter';
import { DateTimeRangeComponent } from '../date-time-range/date-time-range.component';
import { NgSelectApiComponent } from '../ng-select-api/ng-select-api.component';
import { ColorPickerModule } from 'ngx-color-picker';
import { StepperComponent } from '../stepper/stepper.component';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { PermissionsComponent } from '../permissions/permissions.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

const moment = _rollupMoment || _moment;

// See the Moment.js docs for the meaning of these formats:
// https://momentjs.com/docs/#/displaying/format/
export const MY_FORMATS = {
  parse: {
    //   // dateInput: 'LL',
    dateInput: 'DD-MM-YYYY'
  },
  display: {
    dateInput: 'DD-MM-YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

@Component({
  selector: 'app-core-form-content',
  templateUrl: './core-form-content.component.html',
  styleUrls: ['./core-form-content.component.scss'],
  standalone: true,
  imports: [
    ...SHARED_IMPORTS,
    NgSelectModule,
    DrawMapComponent,
    PairsPipe,
    CheckboxsComponent,
    DateTimeComponent,
    DateTimeRangeComponent,
    NgSelectApiComponent,
    StepperComponent,
    PermissionsComponent
  ],
  animations: [
    trigger('showHide', [
      transition('void => *', [
        useAnimation(showAnimation, {
          params: {
            timings: '200ms ease-in-out',
            transform: 'translateY(-10%)',
            opacity: '0'
          }
        })
      ]),
      transition('* => void', [
        useAnimation(HideAnimation, {
          params: {
            timings: '200ms 200ms ease-in-out',
            transform: 'translateY(-10%)',
            opacity: '0'
          }
        })
      ])
    ])
  ],
  providers: [
    // `MomentDateAdapter` can be automatically provided by importing `MomentDateModule` in your
    // application's root module. We provide it at the component level here, due to limitations of
    // our example generation script.
    { provide: MAT_DATE_LOCALE, useValue: 'ar' },

    { provide: DateAdapter, useClass: EnglishMomentDateAdapter, deps: [MAT_DATE_LOCALE] },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS }
  ]
  // changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoreFormContentComponent implements OnInit, AfterViewInit {
  @Input() cid: string;
  @Input() service: RootService;
  @Input() field: ItemProps;
  @Input() forcedFieldType: string;
  @Input() form: FormGroup;
  @Input() isEdit: boolean;
  @Input() isClone: boolean;
  @Input() isSubmitted: boolean;
  @Input() controller: any;
  @Input() stepperMode: boolean;
  @Input() arrayIndex: number = 0;
  @Output() showHideChanges: EventEmitter<any> = new EventEmitter();
  @Output() UpdateFormFieldValueEmitter: EventEmitter<any> = new EventEmitter();

  // form: FormGroup = new FormGroup({});

  previewImage: string;
  imageFieldName: string;
  itemId: number;
  passwordHide = true;
  confirmPasswordHide = true;
  selectedFiles: any = {};
  constructor(
    // private changeDetectorRef: ChangeDetectorRef, // private controlContainer: ControlContainer,
    private destroyRef: DestroyRef,
    private translate?: TranslateService
  ) {}

  ngOnInit() {
    // this.form.valueChanges.subscribe((data: any) => {
    //   console.log(data);
    // });
    // console.log('steeperMode', this.stepperMode);
    // console.log('service in form content ', this.service);
    // console.log('form', this.form);
    // console.log('field', this.field);
  }

  intToFloat(num: number, decPlaces: number) {
    if (num && decPlaces) {
      return num.toFixed(decPlaces);
    } else {
      return;
    }
  }

  type(e: any, noSpaces: boolean) {
    if (e.keyCode === 32) return !noSpaces;
  }

  getValue(obj: any, path: string) {
    return AppHelper.deepFind(obj, path);
  }

  tooltipConditions(tooltip?: any, list?: any) {
    // const tooltip = list.icon.tooltip;
    if (!tooltip) {
      return null;
    } else {
      if (tooltip.text) {
        if (tooltip.translated) {
          return this.translate.instant(tooltip.text);
        } else {
          return tooltip.text;
        }
      } else {
        return null;
      }
    }
  }

  fieldShowConditions(field: any) {
    if (field.showIf) {
      const validators = field.form.Validators;
      let trueCount = 0;
      field.showIf.forEach((condition: any) => {
        if (this.form.controls[Object.keys(condition)[0]].value === Object.values(condition)[0]) {
          trueCount++;
        }
      });
      if (trueCount > 0) {
        // this.form.addControl(field.name,new FormControl('', validators) );
        this.form.get(field.name).setValidators(validators);
        // this.form.updateValueAndValidity();
        this.showHideChanges.emit({ field: field.name, validators });
        this.form.updateValueAndValidity();

        // console.log(validators);

        // console.log(field.name);
        // console.log(this.form.get(field.name).invalid);

        return true;
      } else {
        this.form.get(field.name).clearValidators();
        // this.form.removeControl(field.name);
        // this.form.updateValueAndValidity();
        this.showHideChanges.emit({ field: field.name, validators: null });
        this.form.updateValueAndValidity();

        // console.log(field.name);
        // console.log(this.form.get(field.name).invalid);

        return false;
      }
    } else {
      return true;
    }
  }

  /**
   * checks if the control name is required or not from the current validators on it
   * why > because template checking doesn't update as you remove
   * the validations and re insert them when live removing inputs
   * also > to be able to see which inputs are required before they are
   *  touched and have error.required in validation errors
   * @param controlName the control name in the form
   */
  checkRequiredFields(controlName: string) {
    // if (!this.isEdit) {
    const abstractControl = this.form.controls[controlName];
    if (!abstractControl) {
      return false;
    }
    if (abstractControl.validator) {
      const validator = abstractControl.validator({} as AbstractControl);
      if (validator && validator.required) {
        return true;
      }
    }
    return false;
    // }
  }

  checkImgSrc(field: any) {
    if (!!this.form.controls[field.name].valueChanges) {
      if (!this.previewImage) {
        if (!this.form.controls[field.name].value) {
          return field.form.placeHolder;
        } else {
          return this.form.controls[field.name].value;
        }
      } else {
        return this.previewImage;
      }
    }
  }

  /**
   * Pick color
   * @param field: field name
   * @param color: HEX color
   */
  setColor(field: string, color: string) {
    this.form.controls[field].setValue(color);
  }

  uploadPhoto(event: any, field: any, image?: any) {
    // if (this.isEdit) {
    // this.uploadNewPhoto(field, image);
    // const photoControl = this.fb.control(null, []);
    // this.form.addControl('photo', photoControl);
    // }
    if (field.form.inputValueType === 'base64_img') {
      this.imageFieldName = field.name;
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = this.convertImage.bind(this);
        reader.readAsBinaryString(file);
      }
    } else if (field.form.inputValueType === 'formData') {
      const file = event.target.files[0];
      if (file && file.size > 5000000) {
        this.service.shared.toastr.warning(
          this.service.shared.translate.instant('error_codes.photos_file_size_limit'),
          this.service.shared.translate.instant('error_codes.upload_failed')
        );
      } else {
        this.readURL(file);
        this.form.controls[field.name].setValue(file);
        this.form.controls[field.name].markAsDirty();
      }
    }
  }

  uploadFile(event: any, field: any) {
    if (field.form.inputValueType === 'formData') {
      const file = event.target.files[0];
      if (file && file.size > 10000000) {
        this.service.shared.toastr.warning(
          this.service.shared.translate.instant('error_codes.document_file_size_limit'),
          this.service.shared.translate.instant('error_codes.upload_failed')
        );
      } else {
        this.form.controls[field.name].setValue(file);
        this.form.controls[field.name].markAsDirty();
        this.selectedFiles[field.name] = file;
      }
    }
  }

  /**
   * Preview Image
   * @param input: Input
   */
  readURL(input: any) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.previewImage = e.target.result;
    };
    reader.readAsDataURL(input);
  }

  convertImage(readerEvt: any) {
    const binaryString = readerEvt.target.result;
    this.form.controls[this.imageFieldName].setValue(btoa(binaryString));
  }

  uploadNewPhoto(field: any, image: any) {
    const formV = {};
    formV[field.name] = image;
    this.service
      .doCreate(`${this.cid}/${this.itemId}/${field.name}`, this.toFormData(formV))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {});

    //   .then(() => {
    //     this.service.updateResources.next(null);
    //   });
  }

  toFormData<T>(formValue: T) {
    // console.log(formValue);
    const formData = new FormData();

    for (const key of Object.keys(formValue)) {
      const value = formValue[key];
      /**
       * fix for boolean values in formData method
       */
      if (value === true) {
        formData.append(key, '1');
      } else if (value === false) {
        formData.append(key, '0');
      } else {
        formData.append(key, value);
      }
    }
    // console.log(formData);

    return formData;
  }

  focusInputById(id: any) {
    document.getElementById(id).focus();
  }

  clickElementBySelector(selector: any) {
    return document.querySelector(selector)[0].click();
  }

  clearValue(control: AbstractControl) {
    control.setValue(null);
  }

  async enableControl(control: AbstractControl) {
    if (control.disabled) {
      control.enable();
    }
    return;
  }

  async clearImage(control: AbstractControl, id?: string) {
    await this.enableControl(control);

    control.setValue(null);
    control.markAsDirty();
    this.previewImage = null;
  }

  async openFileSelectFromInput(control: AbstractControl, id: string) {
    await this.enableControl(control);
    // console.log(control);
    setTimeout(() => {
      document.getElementById(id).click();
    });
  }

  ngAfterViewInit(): void {
    // this.changeDetectorRef.detectChanges();
    // this.form.updateValueAndValidity();
    // console.log(this.form.controls[this.field.name].value)
  }

  // ngOnChanges(changes: SimpleChanges): void {
  //   console.log(changes);
  // }

  ngSelect_selectAll(control: AbstractControl, listPrefix: string, selectAll: boolean) {
    if (control && this.service.lists[listPrefix]) {
      const selected: number[] = [];
      if (selectAll) {
        Object.values(this.service.lists[listPrefix]).forEach((item: { id: number }) => {
          selected.push(item.id);
        });
      }
      control.setValue(selected);
      control.markAsDirty();
    }
  }
  ngSelect_selectAllPerObject(control: AbstractControl, listPrefix: string, selectAll: boolean) {
    if (control && this.service.lists[listPrefix]) {
      const selected: any[] = [];
      if (selectAll) {
        Object.values(this.service.lists[listPrefix]).forEach((item: any) => {
          selected.push(item);
        });
      }
      control.setValue(selected);
      control.markAsDirty();
    }
  }
  ngSelect_selectAllLabels(control: AbstractControl, listPrefix: string, selectAll: boolean) {
    if (control && this.service.lists[listPrefix]) {
      const selected: string[] = [];
      if (selectAll) {
        Object.values(this.service.lists[listPrefix]).forEach((item: { name: string }) => {
          selected.push(item.name);
        });
      }
      control.setValue(selected);
    }
  }

  checkSelected(control: AbstractControl, id: number): boolean {
    if (control && id && control.value) {
      if (control.value.find((x: number) => x === id)) {
        return true;
      }
    }
  }
  checkObjectSelected(control: AbstractControl, item: any): boolean {
    // console.log(control.value);
    // console.log(item);

    if (control && item && control.value) {
      if (control.value.find((x: any) => x.id === item.id)) {
        return true;
      }
    }
  }

  checkLabelSelected(control: AbstractControl, name: string): boolean {
    if (control && name && control.value) {
      if (control.value.find((x: string) => x === name)) {
        return true;
      }
    }
  }
  searchInObject = (term: string, item: any) => {
    term = term.toLocaleLowerCase();
    if (item.name.includes(term)) {
      return item;
    }
  };
  preventDefault(event: any) {
    event.preventDefault();
  }

  onChangeValue_ng_select(column: ItemProps, event: any) {
    this.service.onChangeValue_ng_select(column, event);
  }

  UpdateFormFieldValue(event: any, field?: ItemProps) {
    this.UpdateFormFieldValueEmitter.emit({
      fieldProps: field ? field : this.field,
      fieldValue: event.fieldValue,
      updateForm: event.updateForm
    });
  }

  writeInSelect(e: any, field: any) {
    if (field.form.numberOnly) {
      return (e.keyCode > 96 && e.keyCode <= 105) || e.keyCode === 8 || (e.keyCode >= 48 && e.keyCode <= 57);
    }
  }

  addTagNameFn(name: any) {
    // console.log(name);

    name = name.replace(/ /g, '-');

    if (name.length > 20) {
      name = name.substring(0, 20);
    }

    return { name: name, tag: true };
  }

  trackByFn(item: any) {
    return item.id;
  }
  getNameById(id: number, listPrefix: string) {
    if (this.service.lists[listPrefix]) {
      let arr = JSON.parse(JSON.stringify(this.service.lists[listPrefix]));
      return arr.find((e: any) => e.id === id).name;
    }
  }
  unSelect(id: number, fieldName: string) {
    this.form.patchValue({
      [fieldName]: this.form.controls[fieldName].value.filter((d: any) => d !== id)
    });
    this.form.controls[fieldName].markAsDirty();
  }
  //input type float number only [1.5,1.55,2.66]
  writeFloatNumber(event: any) {
    // prevent keyup if  first number is 0
    if (event.key == 0 && !event.target.value) return false;
    if (event.target.value.includes('.') && event.charCode == 46) {
      return false;
    }

    // if (event.key == 0 && /\d/.test(event.target.value)) return false
    // if (event.target.value.includes('.')) {
    //   let terms = event.target.value.split('.');
    //   if (terms[1].length == 3) {
    //     return false;
    //   }
    // } else
    return event.charCode == 8 || event.charCode == 0 || event.charCode == 13
      ? null
      : (event.charCode >= 48 && event.charCode <= 57) || event.charCode == 46;
  }

  // check on field.form.minValue.value and field.form.maxValue field.form.minValue.value
  checkRangeValues(event: any, field: any, minValue?: any, maxValue?: any) {
    const targetNumber = event.target.value;
    if (maxValue && +targetNumber > maxValue) {
      this.form.get(field.name).setValue(maxValue);
    }

    // if (minValue && minValue > targetNumber) {
    //   this.form.get(field.name).setValue(minValue);
    // } else if (maxValue && maxValue < targetNumber) {
    //   this.form.get(field.name).setValue(maxValue);
    // }
  }
  // input type  number only and first number not 0 [1,2,3,4,.....]
  numberOnly(event: any, field: any) {
    // if first input is 0
    if (event.key == 0 && !event.target.value) return field.name.includes('phone');
    else
      return event.charCode == 8 || event.charCode == 0 || event.charCode == 13
        ? false
        : event.charCode >= 48 && event.charCode <= 57;
  }

  getFormControl(name: string, path: string = null) {
    // get the name string value from the mapper
    const fieldName = path || this.service.fieldMapper(name);
    // if not found return with form getter to throw form error
    // the name should be changed to a valid one then
    // if found but its a single value, return it
    if (!fieldName || fieldName.split('.').length == 1) return this.form.get(name);
    const map = fieldName.split('.');

    const formControl = this.form.get(map[2]);
    return formControl;
  }

  enableDropDown(event: any, field: ItemProps) {
    // console.log('event', event);
    // console.log('field', field);
    if (event.checked) {
      this.form.get(field.name).enable();
      this.form.get(field.name).setValidators(Validators.required);
      this.form.get(field.name).updateValueAndValidity();
    } else {
      this.form.get(field.name).disable();
      this.form.get(field.name).setValue('');
    }
  }

  drop(list: any[], event: CdkDragDrop<string[]>) {
    moveItemInArray(list, event.previousIndex, event.currentIndex);
  }

  onScrollToEnd(field: any, event: any) {
    this.service.onScrollToEnd(field, event);
  }
}
